import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { getReport, ReportSummary } from "../api/reports";
import LoadingSpinner from "../components/LoadingSpinner";

const POLL_INTERVAL_MS = 5000;

export default function ReportView() {
  const { reportId } = useParams<{ reportId: string }>();
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!reportId) return;

    let cancelled = false;

    async function tick() {
      try {
        const { report: r, markdown: md, pdfUrl: pu } = await getReport(reportId!);
        if (cancelled) return;
        setReport(r);
        if (md !== null) setMarkdown(md);
        if (pu !== null) setPdfUrl(pu);

        const status = r.status || "completed"; // old rows without status are terminal
        if (status === "pending" || status === "running") {
          timerRef.current = window.setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err: any) {
        console.error("getReport failed", err);
        if (cancelled) return;
        const pieces: string[] = [];
        if (err?.response?.status) pieces.push(`HTTP ${err.response.status}`);
        if (err?.response?.data?.error) pieces.push(err.response.data.error);
        if (err?.code) pieces.push(err.code);
        if (err?.message && pieces.length === 0) pieces.push(err.message);
        setError(pieces.join(" · ") || "Could not load report");
      }
    }
    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [reportId]);

  if (error) return <p className="text-red-600">{error}</p>;
  if (!report) return <LoadingSpinner />;

  const status = report.status || "completed";

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Link to="/reports" className="font-display text-xs uppercase tracking-[0.1em] text-maestro-700 hover:text-maestro-900">
          ← All reports
        </Link>
        {status === "completed" && pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-maestro-900 hover:bg-black text-white px-4 py-1.5 font-display text-xs uppercase tracking-wider"
          >
            <span aria-hidden>⬇</span> Download PDF
          </a>
        )}
      </div>

      {(status === "pending" || status === "running") && (
        <div className="bg-white border border-maestro-200 p-6 sm:p-8 mt-4">
          <h1 className="font-display text-xl sm:text-2xl text-maestro-900 mb-1">
            {report.brand_name}
          </h1>
          <p className="text-maestro-700 mb-8 text-sm">
            Class {report.nice_classes.join(", ")}
          </p>
          <LoadingSpinner
            label={
              status === "pending"
                ? "Queued — pipeline is about to start…"
                : "Running clearance — this usually takes 2–5 minutes. You can close this tab and come back later."
            }
          />
          <p className="text-xs text-center text-maestro-400 mt-4">
            Auto-refreshing every 5s.
          </p>
        </div>
      )}

      {status === "failed" && (
        <div className="bg-white border border-red-300 p-6 sm:p-8 mt-4">
          <h1 className="font-display text-xl text-red-700 mb-2">
            Clearance failed
          </h1>
          <p className="text-red-700 text-sm">
            {report.error || "The pipeline failed unexpectedly. Try again."}
          </p>
          <Link
            to="/randes-search"
            className="inline-block mt-4 font-display text-xs uppercase tracking-[0.1em] text-maestro-700 hover:text-maestro-900"
          >
            ← Run a new clearance
          </Link>
        </div>
      )}

      {status === "completed" && markdown !== null && (
        <article className="bg-white border border-maestro-200 p-6 sm:p-10 mt-4 prose max-w-none overflow-x-auto">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
            {markdown}
          </ReactMarkdown>
        </article>
      )}
    </div>
  );
}
